"use client";

import { useMemo } from "react";
import { Pair, PoolRiskApiResponseObject, Token } from "@/utils/types";
import { useDataContext } from "@/contexts/DataContext";

interface TopPoolsProps {
  data: PoolRiskApiResponseObject[];
  pairs: Pair[];
  tokens: Token[];
  stablecoinIds: Set<string>;
  period: string;
}

function parseAPR(aprString: string): number {
  if (!aprString) return 0;
  const n = parseFloat(aprString.replace(/[^0-9.-]+/g, ""));
  return isNaN(n) ? 0 : n;
}

function parseLiquidity(liquidityString: string): number {
  if (!liquidityString) return 0;
  const n = parseFloat(liquidityString.replace(/[^0-9.-]+/g, ""));
  return isNaN(n) ? 0 : n;
}

function parseVolume(volumeString: string): number {
  const n = parseFloat(volumeString.replace(/[^0-9.-]+/g, ""));
  return isNaN(n) ? 0 : n;
}

export function TopPools({ data, pairs, tokens, stablecoinIds, period }: TopPoolsProps) {
  const { buildPoolRoute } = useDataContext();

  function getTokensForPair(pair: Pair) {
    const t0 = tokens.find((t) => t.id === pair.token0);
    const t1 = tokens.find((t) => t.id === pair.token1);
    return { t0, t1 };
  }

  function getPairLabel(pool: PoolRiskApiResponseObject) {
    const p = pairs.find((pr) => pr.id === pool.pairId);
    if (!p) return pool.market;
    const { t0, t1 } = getTokensForPair(p);
    if (!t0 || !t1) return pool.market;
    const t0Label = t0.name.split(":")[0];
    const t1Label = t1.name.split(":")[0];
    return `${t0Label}/${t1Label}`;
  }

  function handleViewDetails(pool: PoolRiskApiResponseObject) {
    const route = buildPoolRoute(pool);
    window.location.href = route;
  }

  const topByAPR = useMemo(() => {
    return data
      .filter((p) => parseLiquidity(p.totalValueLocked) > 10000)
      .sort((a, b) => parseAPR(b.apr) - parseAPR(a.apr))
      .slice(0, 5);
  }, [data]);

  const topByVolume = useMemo(() => {
    return data.slice().sort((a, b) => parseVolume(b.volume) - parseVolume(a.volume)).slice(0, 5);
  }, [data]);

  const stablecoinPools = useMemo(() => {
    const pairMap = new Map(pairs.map((p) => [p.id, p]));
    function isStablecoinPool(id: string) {
      const pp = pairMap.get(id);
      if (!pp) return false;
      return stablecoinIds.has(pp.token0) && stablecoinIds.has(pp.token1);
    }
    const stables = data.filter((pool) => isStablecoinPool(pool.pairId));
    stables.sort((a, b) => parseAPR(b.apr) - parseAPR(a.apr));
    return stables.slice(0, 5);
  }, [data, pairs, stablecoinIds]);

  return (
    <div className="topTokensOverview">
      <div
        className="transform-gpu transition-transform hover:scale-[1.02] anim-fadeSlideInUp-300ms"
        style={{ animationDelay: "0.1s" }}
      >
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
            BEST APR PAIRS
          </div>
          {topByAPR.map((item, i) => {
            const pairLabel = getPairLabel(item);
            const aprVal = parseAPR(item.apr);
            return (
              <div key={item.pairId} className="stat-row" onClick={() => { handleViewDetails(item); }}>
                <span className="stat-rank">#{i + 1}</span>
                <span className="stat-pair">{pairLabel}</span>
                <span className="val-green">{aprVal.toFixed(2)}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="transform-gpu transition-transform hover:scale-[1.02] anim-fadeSlideInUp-300ms"
        style={{ animationDelay: "0.2s" }}
      >
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
            HIGHEST VOLUME PAIRS
          </div>
          {topByVolume.map((item, i) => {
            const pairLabel = getPairLabel(item);
            const volVal = parseVolume(item.volume);
            return (
              <div key={item.pairId} className="stat-row" onClick={() => { handleViewDetails(item); }}>
                <span className="stat-rank">#{i + 1}</span>
                <span className="stat-pair">{pairLabel}</span>
                <span className="val-muted">${volVal.toLocaleString()}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="transform-gpu transition-transform hover:scale-[1.02] anim-fadeSlideInUp-300ms"
        style={{ animationDelay: "0.3s" }}
      >
        <div className="stat-card-wrap">
          <div className="stat-card-head">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            BEST STABLECOIN POOLS
          </div>
          {stablecoinPools.map((item, i) => {
            const pairLabel = getPairLabel(item);
            const aprVal = parseAPR(item.apr);
            return (
              <div key={item.pairId} className="stat-row" onClick={() => { handleViewDetails(item); }}>
                <span className="stat-rank">#{i + 1}</span>
                <span className="stat-pair">{pairLabel}</span>
                <span className="val-green">{aprVal.toFixed(2)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
